import type { IRiskImpactDimension, IRiskThreat, IRiskVulnerability } from '@/lib/constants/riskThreatsVulnerabilities'
import { RISK_IMPACT_DIMENSIONS } from '@/lib/constants/riskThreatsVulnerabilities'

export const RISK_THREAT_NAMES = {
  TC01: 'Error de uso',
  TC02: 'Abuso de derechos o permisos',
  TC03: 'Falsificación de derechos o permisos',
  TC04: 'Negación de acciones',
  TH01: 'Ataque terrorista, sabotaje',
  TH02: 'Ingeniería social',
  TH04: 'Espionaje remoto',
  TH05: 'Escucha de comunicaciones',
  TH06: 'Robo de soportes o documentos',
  TH07: 'Robo de equipos',
  TH08: 'Robo de identidad digital o credenciales',
  TH10: 'Divulgación de información',
  TH11: 'Entrada de datos de fuentes no fiables',
  TH13: 'Manipulación de software',
  TH14: 'Ataques drive-by a través de la web',
  TH15: 'Ataque de repetición, ataque man-in-the-middle',
  TH16: 'Tratamiento no autorizado de datos personales',
  TH17: 'Acceso no autorizado a instalaciones',
  TH19: 'Uso incorrecto de dispositivos',
  TH22: 'Uso de software falsificado o copiado',
  TH23: 'Corrupción de datos',
  TH24: 'Tratamiento ilícito de datos',
  TH25: 'Envío o distribución de malware',
  TI01: 'Fallo de un sistema de suministro',
  TI03: 'Pérdida del suministro eléctrico',
  TI04: 'Fallo de una red de telecomunicaciones',
  TI05: 'Fallo de equipos de telecomunicaciones',
  TN06: 'Fenómeno pandémico o epidémico',
  TO01: 'Falta de personal',
  TO02: 'Falta de recursos',
  TO03: 'Fallo de proveedores de servicios',
  TO04: 'Incumplimiento de leyes o normativas',
  TP01: 'Incendio',
  TP02: 'Agua',
  TQ01: 'Compromiso de la cuenta del proveedor cloud',
  TQ02: 'Configuración cloud incorrecta (S3, IAM, grupos de seguridad)',
  TQ03: 'Explotación de vulnerabilidades en contenedores o serverless',
  TQ04: 'Ataque a la cadena de suministro SaaS',
  TQ05: 'Brecha de seguridad en APIs',
  TQ06: 'Fuga de datos entre tenants',
  TQ07: 'Inyección en funciones serverless',
  TQ08: 'Compromiso del pipeline de CI/CD',
  TQ09: 'Shadow IT y proliferación de SaaS no gestionado',
  TQ10: 'Crecimiento rápido sin revisión de seguridad',
  TQ11: 'Dependencia de personas clave (bus factor)',
  TQ12: 'Prácticas inseguras de teletrabajo',
  TQ13: 'Baja insuficiente de empleados que se van',
  TQ15: 'Incumplimiento del RGPD/LOPDGDD',
  TQ16: 'Actuación sancionadora de la AEPD',
  TQ17: 'Transferencia internacional de datos no conforme',
  TQ20: 'Falta de gestión de cookies y consentimiento',
  TQ21: 'Falta de plan de respuesta a incidentes',
  TQ22: 'Falta de un proceso formal de incorporación en seguridad',
  TQ23: 'Pérdida o robo de dispositivos portátiles',
  TQ24: 'Avería o fin de vida del hardware sin plan de sustitución o mantenimiento',
  TQ26: 'Manipulación de modelos de IA: envenenamiento de datos, inyección de prompts o entradas adversarias',
  TQ27: 'Fallo de modelos de IA: alucinaciones, sesgos o degradación del rendimiento que restan fiabilidad',
  TT01: 'Fallo de dispositivo o sistema',
  TT02: 'Saturación del sistema de información',
  TT03: 'Violación de la mantenibilidad del sistema de información',
} satisfies Record<IRiskThreat, string>

export const RISK_VULNERABILITY_NAMES = {
  VH05: 'Falta de control eficaz de cambios de configuración',
  VH08: 'Almacenamiento sin protección',
  VH11: 'Falta de gestión de dispositivos móviles o borrado remoto',
  VH12: 'Falta de cifrado completo de disco',
  VH13: 'Hardware antiguo o al final de su vida útil sin plan de sustitución',
  VN01: 'Falta de prueba de envío o recepción de mensajes',
  VN02: 'Líneas de comunicación sin protección',
  VN03: 'Tráfico sensible sin protección',
  VN05: 'Punto único de fallo',
  VN06: 'Falta de identificación y autenticación del emisor y el receptor',
  VN07: 'Arquitectura de red insegura',
  VN08: 'Transferencia de contraseñas en claro',
  VN09: 'Gestión de red inadecuada (resiliencia del enrutamiento)',
  VN10: 'Conexiones a redes públicas sin protección',
  VO01: 'Falta de un procedimiento formal de alta y baja de usuarios',
  VO02: 'Falta de un proceso formal de revisión de derechos de acceso',
  VO03: 'Cláusulas de seguridad ausentes o insuficientes en contratos con clientes o terceros',
  VO04: 'Falta de un procedimiento de supervisión de las instalaciones de tratamiento de información',
  VO05: 'Falta de auditorías periódicas',
  VO06: 'Falta de procedimientos de identificación y evaluación de riesgos',
  VO09: 'Acuerdo de nivel de servicio ausente o insuficiente',
  VO13: 'Falta de un proceso formal de autorización de la información de acceso público',
  VO14: 'Asignación inadecuada de responsabilidades de seguridad de la información',
  VO15: 'Falta de planes de continuidad',
  VO17: 'Falta de procedimientos para introducir software en los sistemas en producción',
  VO18: 'Falta de procedimientos para el tratamiento de información clasificada',
  VO26: 'Falta de mecanismos de supervisión de brechas de seguridad',
  VO28: 'Falta de procedimientos para cumplir los derechos de propiedad intelectual',
  VP01: 'Ausencia de personal',
  VP03: 'Formación en seguridad insuficiente',
  VP04: 'Uso incorrecto de software y hardware',
  VP05: 'Falta de concienciación en seguridad',
  VP07: 'Trabajo sin supervisión de personal externo o de limpieza',
  VQ01: 'Falta de gestión de la postura de seguridad cloud (CSPM)',
  VQ02: 'Roles de IAM con permisos excesivos',
  VQ03: 'Aislamiento insuficiente entre tenants',
  VQ04: 'Registro de actividad cloud insuficiente',
  VQ05: 'Falta de análisis de infraestructura como código (IaC)',
  VQ06: 'Almacenamiento cloud accesible públicamente',
  VQ07: 'Falta de segmentación de VPC',
  VQ08: 'Falta de autenticación multifactor (MFA)',
  VQ09: 'Falta de alertas de inicios de sesión anómalos',
  VQ10: 'Secretos incrustados en el código fuente',
  VQ11: 'Falta de un proceso formal de incorporación y salida en seguridad',
  VQ12: 'Falta de transferencia de conocimiento',
  VQ14: 'Protección de endpoints débil',
  VQ15: 'Falta de clasificación de datos',
  VQ16: 'Falta de plan de respuesta a incidentes',
  VQ17: 'Falta de evaluación de seguridad de proveedores',
  VQ19: 'Falta de gobierno de protección de datos (DPD, EIPD)',
  VQ20: 'Registro de actividades de tratamiento incompleto',
  VQ21: 'Falta de un banner de cookies conforme',
  VQ22: 'Falta de gestión del consentimiento',
  VQ23: 'Falta de procedimiento de notificación de brechas',
  VQ24: 'Falta de contratos de encargo de tratamiento con subencargados',
  VQ25: 'Falta de garantías para transferencias internacionales de datos (CCT)',
  VQ26: 'Falta de validación de entradas y pruebas adversarias en IA',
  VQ27: 'Datos de entrenamiento de IA mal gobernados',
  VS01: 'Pruebas de software ausentes o insuficientes',
  VS02: 'Fallos conocidos en el software',
  VS04: 'Eliminación o reutilización de soportes sin borrado seguro',
  VS05: 'Falta de registro de auditoría',
  VS06: 'Asignación incorrecta de derechos de acceso',
  VS09: 'Interfaz de usuario compleja',
  VS11: 'Configuración incorrecta de parámetros',
  VS13: 'Falta de mecanismos de identificación y autenticación',
  VS15: 'Gestión de contraseñas deficiente',
  VS16: 'Servicios innecesarios habilitados',
  VS18: 'Especificaciones poco claras o incompletas para desarrolladores',
  VS19: 'Falta de control de cambios eficaz',
  VS20: 'Descarga y uso de software sin control',
  VS21: 'Falta de copias de seguridad',
  VS22: 'No se generan informes de gestión',
  VS_S01: 'Uso inadecuado o descuidado del control de acceso físico a edificios y salas',
  VS_S03: 'Red eléctrica inestable',
  VS_S04: 'Falta de protección física del edificio, puertas y ventanas',
} satisfies Record<IRiskVulnerability, string>

export const RISK_THREATS_SELECTOR = {
  LABEL: 'Amenazas',
  EMPTY: 'Sin amenaza',
  SEARCH: 'Buscar amenazas',
  NO_RESULTS: 'No se encontraron amenazas',
  SELECT: 'Seleccionar amenazas...',
  REMOVE: 'Quitar',
  SUGGESTIONS_INFO: 'Sugeridas según los activos afectados',
  NO_SUGGESTIONS: 'Añade activos afectados para obtener sugerencias',
}

export const RISK_VULNERABILITIES_SELECTOR = {
  LABEL: 'Vulnerabilidades',
  EMPTY: 'Sin vulnerabilidad',
  SEARCH: 'Buscar vulnerabilidades',
  NO_RESULTS: 'No se encontraron vulnerabilidades',
  SELECT: 'Seleccionar vulnerabilidades...',
  REMOVE: 'Quitar',
  SUGGESTIONS_INFO: 'Sugeridas según las amenazas seleccionadas',
  NO_SUGGESTIONS: 'Selecciona amenazas para obtener sugerencias',
}

export const RISK_IMPACT_DIMENSIONS_SELECTOR = {
  LABEL: 'Dimensiones de impacto',
  DIMENSIONS: {
    [RISK_IMPACT_DIMENSIONS.AVAILABILITY]: { SHORT: 'D', LABEL: 'Disponibilidad' },
    [RISK_IMPACT_DIMENSIONS.CONFIDENTIALITY]: { SHORT: 'C', LABEL: 'Confidencialidad' },
    [RISK_IMPACT_DIMENSIONS.INTEGRITY]: { SHORT: 'I', LABEL: 'Integridad' },
    [RISK_IMPACT_DIMENSIONS.AUTHENTICITY]: { SHORT: 'A', LABEL: 'Autenticidad' },
    [RISK_IMPACT_DIMENSIONS.TRACEABILITY]: { SHORT: 'T', LABEL: 'Trazabilidad' },
  } satisfies Record<IRiskImpactDimension, { SHORT: string; LABEL: string }>,
}
